import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { ProjectionRow } from '../pages/projectionsModel';

/**
 * Projections financières : exercices disponibles, puis lignes de l'exercice choisi.
 * `annee` à null = exercice par défaut fourni par l'API (le plus récent présent en cache).
 */
export function useFinancialProjections(annee: number | null) {
    const { token } = useAuth();
    const [annees, setAnnees] = useState<number[]>([]);
    const [anneeDefaut, setAnneeDefaut] = useState<number | null>(null);
    const [anneesChargees, setAnneesChargees] = useState(false);
    const [data, setData] = useState<ProjectionRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) return;
        let annule = false;
        fetch(`${API_URL}/projections/financial/years`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((json: { data: number[]; meta: { defaut: number | null } }) => {
                if (annule) return;
                setAnnees(json.data);
                setAnneeDefaut(json.meta.defaut);
            })
            .catch(() => { if (!annule) setError('Impossible de charger les exercices disponibles.'); })
            .finally(() => { if (!annule) setAnneesChargees(true); });
        return () => { annule = true; };
    }, [token]);

    const anneeEffective = annee ?? anneeDefaut;

    useEffect(() => {
        if (!token || anneeEffective == null) {
            // Cache vide (aucun exercice) ou erreur : rien à charger, l'écran affiche son état vide.
            if (anneesChargees) setLoading(false);
            return;
        }
        let annule = false;
        setLoading(true);
        setError('');
        fetch(`${API_URL}/projections/financial?year=${anneeEffective}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((json: ProjectionRow[]) => { if (!annule) setData(json); })
            .catch(() => { if (!annule) setError('Impossible de charger les projections.'); })
            .finally(() => { if (!annule) setLoading(false); });
        return () => { annule = true; };
    }, [token, anneeEffective, anneesChargees]);

    return {
        annees,
        annee: anneeEffective,
        data,
        loading,
        error,
        cacheDate: data[0]?._cache_date ?? null,
    };
}
